import { CookieBanner } from 'components/cookie/CookieBanner'
import { useAnalytics } from 'hooks/analytics/useAnalytics'
import { useAnalyticsPageview } from 'hooks/analytics/useAnalyticsPageview'
import { useLocalStorageState } from 'hooks/useStorage'
import React, { useCallback, useContext, useEffect, useMemo } from 'react'
import { noop } from 'util/functions'

const COOKIE_KEY = 'COOKIE_CONTROL'

interface CookieConfigModel {
  cookiesAccepted: boolean
  analyticsAccepted?: boolean
}

interface CookiesAuthorizationProviderProps {
  children: React.ReactNode
}

interface CookieConfigContext {
  cookiesEnabled: boolean
  denyCookies: () => void
}

const CookieContext = React.createContext<CookieConfigContext>({
  cookiesEnabled: false,
  denyCookies: noop,
})

export function CookiesAuthorizationProvider({ children }: Readonly<CookiesAuthorizationProviderProps>) {
  const { initializeAnalytics, disableAnalytics } = useAnalytics()
  const [cookieConfig, setCookieConfig, clearCookieConfig] = useLocalStorageState<CookieConfigModel>(COOKIE_KEY)

  useAnalyticsPageview()

  useEffect(() => {
    if (cookieConfig?.analyticsAccepted) initializeAnalytics()
  }, [cookieConfig, initializeAnalytics])

  // A inicialização fica a cargo do efeito acima, disparado pela mudança da configuração.
  const handleClickAceitarTodos = useCallback(() => {
    setCookieConfig({ cookiesAccepted: true, analyticsAccepted: true })
  }, [setCookieConfig])

  const handleClearCookieConfig = useCallback(() => {
    clearCookieConfig(true)
    disableAnalytics()
  }, [clearCookieConfig, disableAnalytics])

  const handleClickAceitarNecessarios = useCallback(() => {
    handleClearCookieConfig()
    setCookieConfig({ cookiesAccepted: true, analyticsAccepted: false })
  }, [handleClearCookieConfig, setCookieConfig])

  const valueCookieConfig: CookieConfigContext = useMemo(
    () => ({
      cookiesEnabled: cookieConfig?.cookiesAccepted,
      denyCookies: handleClearCookieConfig,
    }),
    [cookieConfig, handleClearCookieConfig]
  )

  return (
    <CookieContext.Provider value={valueCookieConfig}>
      {cookieConfig?.analyticsAccepted === undefined && (
        <CookieBanner
          onClickAceitarTodos={handleClickAceitarTodos}
          onClickAceitarNecessarios={handleClickAceitarNecessarios}
        />
      )}
      {children}
    </CookieContext.Provider>
  )
}

export const useCookieConfig = () => useContext(CookieContext)
